/**
 * HLX Provider View Page
 *
 * Employer-facing read-only view of a provider's credentials,
 * with masked/unmasked projection and consent-based unmasking.
 */

import { useMachine, useSelector } from '@xstate/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '~/app';
import { Tabs } from '~/components/Tabs/Tabs';
import { useFeatureFlag } from '~/features/feature-flags/useFeatureFlag';
import {
  MarketActionRow,
  MarketContentPanel,
  MarketEmptyPanel,
  MarketLoadingPanel,
  MarketPanelHeader,
  MarketStatusPill,
} from '~/features/shared/market-content';
import {
  ResponseUnmaskPanel,
  selectUnmaskViewerScope,
} from '~/features/shared/market-content/ResponseUnmaskPanel';
import { dateStringFormatter } from '~/features/utils';
import {
  buildB2BOpportunitiesUrl,
  buildJobOpportunitiesUrl,
  buildServiceOpportunitiesUrl,
  paths,
} from '~/routing';
import { providerViewMachine } from './providerView.machine';
import type {
  AreaOfInterest,
  BoardCertRecord,
  CdsLicenseRecord,
  CertificationRecord,
  DeaLicenseRecord,
  EducationRecord,
  EmploymentRecord,
  Language,
  MedicalLicenseRecord,
  ProviderViewResponse,
  ProviderViewTab,
  TrainingRecord,
} from './types';

const TAB_DEFS: Array<{ name: string; key: ProviderViewTab; cyTag: string }> = [
  { name: 'Overview', key: 'overview', cyTag: 'tab-overview' },
  { name: 'Credentials', key: 'credentials', cyTag: 'tab-credentials' },
  { name: 'Documents', key: 'documents', cyTag: 'tab-documents' },
];

const formatProviderViewDate = (value?: string | null) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return dateStringFormatter(value, 'SHORT_DATE');
};

const joinVisibleParts = (parts: Array<string | null | undefined>) => {
  const visibleParts = parts.filter((part): part is string => Boolean(part));
  return visibleParts.length > 0 ? visibleParts.join(', ') : null;
};

export const ProviderView = () => {
  const viewerScopeKey = useSelector(UserContext.useActorRef(), selectUnmaskViewerScope);
  const [searchParams] = useSearchParams();
  const providerIdParam = searchParams.get('providerId');
  const providerId = providerIdParam ? Number(providerIdParam) : null;
  const responseIdParam = searchParams.get('responseId');
  const responseId = responseIdParam ? Number(responseIdParam) : null;
  const responseProviderIdParam = searchParams.get('responseProviderId');
  const responseProviderId = responseProviderIdParam ? Number(responseProviderIdParam) : null;
  const sideParam = searchParams.get('side');
  const side = sideParam === 'buying' || sideParam === 'selling' ? sideParam : null;
  const serviceListingIdParam = searchParams.get('serviceListingId');
  const serviceListingId = serviceListingIdParam ? Number(serviceListingIdParam) : null;

  const isValidProviderId = providerId !== null && !Number.isNaN(providerId) && providerId > 0;
  const isValidResponseId = responseId !== null && !Number.isNaN(responseId) && responseId > 0;
  const isValidResponseProviderId =
    responseProviderId !== null && !Number.isNaN(responseProviderId) && responseProviderId > 0;
  const isValidServiceListingId =
    serviceListingId !== null && Number.isInteger(serviceListingId) && serviceListingId > 0;

  if (
    (!isValidProviderId || !isValidResponseId) &&
    !isValidResponseProviderId &&
    !isValidServiceListingId
  ) {
    return (
      <MarketContentPanel dataCy="provider-view-error" variant="empty">
        <MarketEmptyPanel title="Missing or invalid provider response" />
      </MarketContentPanel>
    );
  }

  return (
    <ProviderViewContent
      key={`${viewerScopeKey}:${providerId}:${responseId}:${responseProviderId}:${serviceListingId}`}
      viewerScopeKey={viewerScopeKey}
      providerId={isValidProviderId ? providerId : null}
      responseId={isValidResponseId ? responseId : null}
      responseProviderId={isValidResponseProviderId ? responseProviderId : null}
      serviceListingId={isValidServiceListingId ? serviceListingId : null}
      side={side}
    />
  );
};

const ProviderViewContent = ({
  viewerScopeKey,
  providerId,
  responseId,
  responseProviderId,
  serviceListingId,
  side,
}: {
  viewerScopeKey: string;
  providerId: number | null;
  responseId: number | null;
  responseProviderId: number | null;
  serviceListingId: number | null;
  side: 'buying' | 'selling' | null;
}) => {
  // [MCV-1889] The Responses tab the response came from; with the flag off there are no tabs.
  const offeringEnabled = useFeatureFlag('b2bOfferingListings');
  const [state, send] = useMachine(providerViewMachine, {
    input: { providerId, responseId, responseProviderId, serviceListingId },
  });
  const navigate = useNavigate();

  const { data, activeTab, error } = state.context;

  // Hash-based tab navigation
  const [tabIndex, setTabIndex] = useState(0);
  const handleTabChange = useCallback(
    (index: number) => {
      setTabIndex(index);
      send({ type: 'ON_TAB_CHANGE', payload: { tab: TAB_DEFS[index].key } });
      window.location.hash = TAB_DEFS[index].key;
    },
    [send]
  );

  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as ProviderViewTab;
    const idx = TAB_DEFS.findIndex((t) => t.key === hash);
    if (idx >= 0) {
      setTabIndex(idx);
      send({ type: 'ON_TAB_CHANGE', payload: { tab: TAB_DEFS[idx].key } });
    }
  }, [send]);

  const tabs = useMemo(
    () =>
      TAB_DEFS.map((t, i) => ({
        name: t.name,
        current: i === tabIndex,
        cyTag: t.cyTag,
      })),
    [tabIndex]
  );

  // Documents tab visible after bilateral acceptance or grandfathered ACCEPTED rows
  const showDocuments =
    (data?.unmaskState.providerAcceptedAt != null &&
      data?.unmaskState.employerAcceptedAt != null) ||
    data?.unmaskState.status === 'ACCEPTED';

  const filteredTabs = showDocuments
    ? tabs
    : tabs.filter((_, i) => TAB_DEFS[i].key !== 'documents');

  // Clamp to visible tab if documents deep link resolves before data arrives
  useEffect(() => {
    if (!showDocuments && activeTab === 'documents') {
      setTabIndex(0);
      send({ type: 'ON_TAB_CHANGE', payload: { tab: 'overview' } });
    }
  }, [showDocuments, activeTab, send]);

  if (state.matches('$_LOADING')) {
    return (
      <MarketContentPanel dataCy="provider-view-loading" variant="empty">
        <MarketLoadingPanel label="Loading provider view" />
      </MarketContentPanel>
    );
  }

  if (state.matches('$_ERROR') || !data) {
    return (
      <MarketContentPanel dataCy="provider-view-error" variant="empty">
        <MarketEmptyPanel title={error || 'Failed to load provider view'} />
        <MarketActionRow
          className="mt-4"
          align="start"
          actions={[
            {
              label: 'Retry',
              colorVariant: 'secondary',
              onClick: () => send({ type: 'ON_RETRY' }),
            },
          ]}
        />
      </MarketContentPanel>
    );
  }

  const header = data.header;
  const unmask = data.unmaskState;

  const backUrl = (() => {
    if (unmask.responseId) {
      if (unmask.listingType === 'JOB') {
        return buildJobOpportunitiesUrl({ responseId: unmask.responseId });
      }
      if (unmask.listingType === 'MSAAS' && unmask.viewerParty) {
        return buildB2BOpportunitiesUrl({
          view: unmask.viewerParty === 'LISTING_OWNER' ? 'received' : 'sent',
          responseId: unmask.responseId,
          side: (offeringEnabled && side) || undefined,
        });
      }
      if (unmask.listingType === 'SERVICE') {
        return buildServiceOpportunitiesUrl(unmask.responseId);
      }
    }
    if (responseId) return buildJobOpportunitiesUrl({ responseId });
    // A listing-scoped view has no response; it was opened from a search row.
    if (serviceListingId) return paths.hlx.search;
    return null;
  })();

  return (
    <div data-cy="provider-view-page" className="text-left">
      {backUrl && (
        <div className="mb-4 flex justify-start">
          <Link
            className="text-left text-sm font-semibold text-primary-700 hover:underline"
            to={backUrl}
            data-cy="provider-view-back"
          >
            {serviceListingId && !responseId ? 'Back to search' : 'Back to application'}
          </Link>
        </div>
      )}
      {/* Header */}
      <MarketContentPanel className="mb-6" dataCy="provider-view-header">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-content-primary" data-cy="provider-display-name">
              {header.displayName}
            </h1>
            <p className="mt-1 text-sm text-content-secondary">
              {header.providerType} / {header.providerCategory}
            </p>
            {header.verificationLevel != null && (
              <p className="mt-1 text-sm text-content-secondary">
                Verification: {'★'.repeat(header.verificationLevel)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {header.masked ? (
              <MarketStatusPill status="masked" label="MASKED" cyTag="badge-masked" />
            ) : (
              <MarketStatusPill status="unmasked" label="UNMASKED" cyTag="badge-unmasked" />
            )}
          </div>
        </div>

        {/* Unmask action buttons */}
        {unmask.responseId && (
          <div className="mt-4" data-cy="unmask-actions">
            <ResponseUnmaskPanel
              responseId={unmask.responseId}
              viewerScopeKey={viewerScopeKey}
              onChanged={() =>
                send({
                  type: 'ON_LOAD',
                  payload: {
                    providerId: providerId ?? undefined,
                    responseId: responseId ?? undefined,
                    responseProviderId: responseProviderId ?? undefined,
                    serviceListingId: serviceListingId ?? undefined,
                  },
                })
              }
            />
            {(unmask.status === 'NEGOTIATING' || unmask.status === 'PENDING_ACCEPTANCE') && (
              <>
                <p className="mb-3 text-sm">
                  When both parties accept the current offer, permitted identities are shared for
                  this response.
                </p>
                <MarketActionRow
                  actions={[
                    {
                      label: 'Review current offer',
                      cyTag: 'btn-review-offer',
                      onClick: () => {
                        if (!unmask.responseId) return;
                        if (unmask.listingType === 'JOB') {
                          navigate(buildJobOpportunitiesUrl({ responseId: unmask.responseId }));
                        } else if (unmask.listingType === 'MSAAS') {
                          if (!unmask.viewerParty) return;
                          navigate(
                            buildB2BOpportunitiesUrl({
                              view: unmask.viewerParty === 'LISTING_OWNER' ? 'received' : 'sent',
                              responseId: unmask.responseId,
                              side: (offeringEnabled && side) || undefined,
                            })
                          );
                        } else if (unmask.listingType === 'SERVICE') {
                          navigate(buildServiceOpportunitiesUrl(unmask.responseId));
                        }
                      },
                      disabled:
                        !unmask.listingType ||
                        (unmask.listingType === 'MSAAS' && !unmask.viewerParty),
                      colorVariant: 'secondary',
                    },
                  ]}
                />
              </>
            )}
          </div>
        )}
      </MarketContentPanel>

      {/* Tabs */}
      <Tabs handleTabChange={handleTabChange} activeTab={tabIndex} tabs={filteredTabs} />

      {/* Tab content */}
      <div className="mt-6">
        {activeTab === 'overview' && <OverviewSection data={data} />}
        {activeTab === 'credentials' && <CredentialsSection data={data} />}
        {activeTab === 'documents' && showDocuments && (
          <MarketContentPanel dataCy="documents-section" variant="empty">
            <MarketEmptyPanel title="No documents are available yet." />
          </MarketContentPanel>
        )}
      </div>
    </div>
  );
};

// -------------------------------------------------------------------
// Overview Section
// -------------------------------------------------------------------

const OverviewSection = ({ data }: { data: ProviderViewResponse }) => {
  const { overview, header } = data;

  return (
    <div className="space-y-6" data-cy="overview-section">
      {/* Professional Info */}
      <SectionCard title="Professional Information" cyTag="section-professional">
        <FieldRow label="Provider Type" value={header.providerType} />
        <FieldRow label="Category" value={header.providerCategory} />
        {header.npiNum && <FieldRow label="NPI" value={header.npiNum} />}
        {overview.languages?.length > 0 && (
          <FieldRow
            label="Languages"
            value={overview.languages.map((l: Language) => l.language).join(', ')}
          />
        )}
        {overview.areasOfInterest?.length > 0 && (
          <FieldRow
            label="Areas of Interest"
            value={overview.areasOfInterest.map((a: AreaOfInterest) => a.areaOfInterest).join(', ')}
          />
        )}
      </SectionCard>

      {/* Contact Info (unmasked only) */}
      {overview.contactInfo && (
        <SectionCard title="Contact Information" cyTag="section-contact">
          {overview.contactInfo.emails?.map(
            (e: { email: string | null; isPrimary: boolean | null }, i: number) => (
              <FieldRow key={i} label={`Email${e.isPrimary ? ' (Primary)' : ''}`} value={e.email} />
            )
          )}
          {overview.contactInfo.phones?.map(
            (
              p: {
                phoneNumber: string | null;
                phoneType: string | null;
                isPrimary: boolean | null;
              },
              i: number
            ) => (
              <FieldRow
                key={i}
                label={`Phone${p.isPrimary ? ' (Primary)' : ''}`}
                value={p.phoneNumber}
              />
            )
          )}
        </SectionCard>
      )}

      {/* Education */}
      <SectionCard title="Education" cyTag="section-education">
        {overview.education?.length === 0 && <EmptyState label="education records" />}
        {overview.education?.map((ed: EducationRecord) => (
          <div key={ed.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="Institution" value={ed.institution ?? '[hidden]'} masked />
            <FieldRow label="Degree" value={ed.degree} />
            <FieldRow label="Field of Study" value={ed.fieldOfStudy} />
            <FieldRow label="Start Date" value={formatProviderViewDate(ed.startDate)} />
            <FieldRow label="End Date" value={formatProviderViewDate(ed.endDate)} />
            <FieldRow label="Graduation Date" value={formatProviderViewDate(ed.graduationDate)} />
          </div>
        ))}
      </SectionCard>

      {/* Employment */}
      <SectionCard title="Employment" cyTag="section-employment">
        {overview.employment?.length === 0 && <EmptyState label="employment records" />}
        {overview.employment?.map((emp: EmploymentRecord) => {
          const location = joinVisibleParts([emp.city, emp.state]);
          return (
            <div key={emp.id} className="border-b border-grey-900 py-3 last:border-0">
              <FieldRow label="Position" value={emp.position} />
              <FieldRow
                label="Employer"
                value={emp.employerName ?? '[hidden]'}
                masked={!emp.employerName}
              />
              <FieldRow label="Department" value={emp.department} />
              {location && <FieldRow label="Location" value={location} />}
              <FieldRow label="Start Date" value={formatProviderViewDate(emp.startDate)} />
              <FieldRow label="End Date" value={formatProviderViewDate(emp.endDate)} />
              {emp.isCurrent && <MarketStatusPill status="active" label="Current" />}
            </div>
          );
        })}
      </SectionCard>
    </div>
  );
};

// -------------------------------------------------------------------
// Credentials Section
// -------------------------------------------------------------------

const CredentialsSection = ({ data }: { data: ProviderViewResponse }) => {
  const { credentials } = data;

  return (
    <div className="space-y-6" data-cy="credentials-section">
      <SectionCard title="Medical Licenses" cyTag="section-medical-licenses">
        {credentials.medicalLicenses?.length === 0 && <EmptyState label="medical licenses" />}
        {credentials.medicalLicenses?.map((lic: MedicalLicenseRecord) => (
          <div key={lic.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="State" value={lic.state} />
            <FieldRow label="Status" value={lic.status} />
            <FieldRow
              label="License #"
              value={lic.licenseNumber ?? '[hidden]'}
              masked={!lic.licenseNumber}
            />
            <FieldRow label="Issue Date" value={formatProviderViewDate(lic.issueDate)} />
            <FieldRow label="Expires" value={formatProviderViewDate(lic.expirationDate)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Board Certifications" cyTag="section-board-certs">
        {credentials.boardCertifications?.length === 0 && (
          <EmptyState label="board certifications" />
        )}
        {credentials.boardCertifications?.map((bc: BoardCertRecord) => (
          <div key={bc.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="Specialty" value={bc.specialty} />
            {bc.subspecialty && (
              <FieldRow
                label="Subspecialty"
                value={
                  bc.specialtyBoardCode
                    ? `${bc.subspecialty} (${bc.specialtyBoardCode})`
                    : bc.subspecialty
                }
              />
            )}
            <FieldRow label="Board" value={bc.certifyingBoard} />
            <FieldRow label="Status" value={bc.status} />
            <FieldRow
              label="Cert #"
              value={bc.certificationNumber ?? '[hidden]'}
              masked={!bc.certificationNumber}
            />
            <FieldRow label="Expires" value={formatProviderViewDate(bc.expirationDate)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="DEA Licenses" cyTag="section-dea-licenses">
        {credentials.deaLicenses?.length === 0 && <EmptyState label="DEA licenses" />}
        {credentials.deaLicenses?.map((dea: DeaLicenseRecord) => (
          <div key={dea.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="State" value={dea.state} />
            <FieldRow label="Schedule" value={dea.schedule} />
            <FieldRow label="DEA #" value={dea.deaNumber ?? '[hidden]'} masked={!dea.deaNumber} />
            <FieldRow label="Expires" value={formatProviderViewDate(dea.expirationDate)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="CDS Licenses" cyTag="section-cds-licenses">
        {credentials.cdsLicenses?.length === 0 && <EmptyState label="CDS licenses" />}
        {credentials.cdsLicenses?.map((cds: CdsLicenseRecord) => (
          <div key={cds.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="State" value={cds.state} />
            <FieldRow
              label="License #"
              value={cds.licenseNumber ?? '[hidden]'}
              masked={!cds.licenseNumber}
            />
            <FieldRow label="Expires" value={formatProviderViewDate(cds.expirationDate)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Certifications" cyTag="section-certifications">
        {credentials.certifications?.length === 0 && <EmptyState label="certifications" />}
        {credentials.certifications?.map((cert: CertificationRecord) => (
          <div key={cert.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="Certification" value={cert.certificationName} />
            <FieldRow label="Organization" value={cert.issuingOrganization} />
            <FieldRow
              label="Cert #"
              value={cert.certificationNumber ?? '[hidden]'}
              masked={!cert.certificationNumber}
            />
            <FieldRow label="Expires" value={formatProviderViewDate(cert.expirationDate)} />
          </div>
        ))}
      </SectionCard>

      <SectionCard title="Training" cyTag="section-training">
        {credentials.training?.length === 0 && <EmptyState label="training records" />}
        {credentials.training?.map((tr: TrainingRecord) => (
          <div key={tr.id} className="border-b border-grey-900 py-3 last:border-0">
            <FieldRow label="Program" value={tr.programType} />
            <FieldRow label="Institution" value={tr.institution ?? '[hidden]'} masked />
            <FieldRow label="Specialty" value={tr.specialty} />
            <FieldRow label="Start Date" value={formatProviderViewDate(tr.startDate)} />
            <FieldRow label="End Date" value={formatProviderViewDate(tr.endDate)} />
          </div>
        ))}
      </SectionCard>
    </div>
  );
};

// -------------------------------------------------------------------
// Shared UI primitives
// -------------------------------------------------------------------

const SectionCard = ({
  title,
  cyTag,
  children,
}: {
  title: string;
  cyTag: string;
  children: React.ReactNode;
}) => (
  <MarketContentPanel dataCy={cyTag}>
    <MarketPanelHeader title={title} className="mb-4" />
    {children}
  </MarketContentPanel>
);

const FieldRow = ({
  label,
  value,
  masked,
}: {
  label: string;
  value: string | number | null | undefined;
  masked?: boolean;
}) => (
  <div className="flex flex-col gap-1 sm:flex-row sm:gap-0 sm:items-start py-1 text-left">
    <span className="sm:w-40 shrink-0 text-left text-sm font-medium text-content-secondary">
      {label}
    </span>
    <span
      className={`min-w-0 [overflow-wrap:anywhere] text-sm ${masked ? 'text-grey-400' : 'text-grey-100'}`}
    >
      {value ?? '—'}
    </span>
  </div>
);

// Left-aligned local placeholder: the shared MarketEmptyPanel/EmptyState center
// their content (by design, for stand-alone empty panels), which is wrong for
// these inline Overview/Credentials sections. Kept local rather than changing
// the shared components other callers still rely on being centered.
const EmptyState = ({ label }: { label: string }) => (
  <p className="py-3 text-left text-sm text-content-secondary">No {label} on file</p>
);

export default ProviderView;
